import './index.scss'

import { useState } from 'react'

import Modal from '@components/Modal'
import { EditableLaunchpadLink, LaunchpadLinksEditor } from '../useLaunchpadLinks'

import closeIcon from 'url:@icon/close.svg';
import checkmarkIcon from 'url:@icon/checkmark.svg';

// The whole link editor lives here: the detail page only shows what the links
// resolve to. Closing without saving discards the draft, so the modal reads as
// a single edit transaction.
export default function LinksEditorModal(props: {
  isOpen: boolean,
  editor: LaunchpadLinksEditor,
  close: () => void
}) {
  const { links, hasUnsavedChanges, isSaving, error } = props.editor;
  const [draggedLinkID, setDraggedLinkID] = useState<string | null>(null);

  const closeAndDiscard = () => {
    props.editor.reset();
    props.close();
  };

  return <Modal
    id='links-editor-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='links-editor-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          disabled={isSaving}
          onClick={closeAndDiscard}>
          <img src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>Edit Links</h3>

        <button
          type='button'
          className={isSaving ? 'header-control confirm saving' : 'header-control confirm'}
          disabled={isSaving || !hasUnsavedChanges}
          onClick={async () => {
            await props.editor.save();
            props.close();
          }}>
          {isSaving ?
            <span className='header-control-spinner' role='status' aria-label='Saving' /> :
            <img src={checkmarkIcon} alt='Save icon' />}
        </button>
      </div>

      <div className='body'>
        <p className='caption'>
          These appear at the top of Launchpad, in the order set below — drag a link by its handle to move it.
          On the Salt mobile app they appear at the bottom.
        </p>

        {error ? <p className='save-error'>{error}</p> : null}

        {links.length === 0 ? <p className='empty-links-state caption'>No links added.</p> : <div className='links-list'>
          {links.map((link, index) => (
            <LinkRow
              key={link.id}
              link={link}
              position={index + 1}
              isDragging={draggedLinkID === link.id}
              onChange={(changes) => props.editor.updateLink(link.id, changes)}
              onRemove={() => props.editor.removeLink(link.id)}
              onDragStart={() => setDraggedLinkID(link.id)}
              onDragEnd={() => setDraggedLinkID(null)}
              onDropOn={() => {
                if (draggedLinkID && draggedLinkID !== link.id) {
                  props.editor.moveLink(draggedLinkID, link.id);
                }

                setDraggedLinkID(null);
              }}
            />
          ))}
        </div>}

        <button type='button' className='add-link-button' disabled={isSaving} onClick={props.editor.addLink}>
          Add Link
        </button>

      </div>
    </div>
  </Modal>
}

function LinkRow(props: {
  link: EditableLaunchpadLink;
  position: number;
  isDragging: boolean;
  onChange: (changes: Partial<EditableLaunchpadLink>) => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropOn: () => void;
}) {
  return <div
    className={props.isDragging ? 'link-card is-dragging' : 'link-card'}
    onDragOver={(event) => event.preventDefault()}
    onDrop={props.onDropOn}>
    {/* Only the handle is draggable, so dragging inside a field selects text
        rather than picking up the whole row. */}
    <span
      className='drag-handle'
      draggable
      role='button'
      aria-label={`Reorder link ${props.position}`}
      onDragStart={props.onDragStart}
      onDragEnd={props.onDragEnd}>
      ⠿
    </span>

    <div className='link-fields'>
      <input
        id={`launchpad-link-name-${props.link.id}`}
        type='text'
        className='link-name'
        aria-label={`Link ${props.position} name`}
        placeholder='Link Name'
        value={props.link.name}
        onChange={(event) => props.onChange({ name: event.target.value })}
      />

      <input
        id={`launchpad-link-url-${props.link.id}`}
        type='text'
        className='link-url'
        aria-label={`Link ${props.position} URL`}
        placeholder='Link URL'
        value={props.link.url}
        onChange={(event) => props.onChange({ url: event.target.value })}
      />
    </div>

    <button
      type='button'
      className='remove-link-button'
      aria-label={`Remove link ${props.position}`}
      onClick={props.onRemove}>
      Remove
    </button>
  </div>
}
